import dayjs from 'dayjs';
import Cookie from 'js-cookie';
import { assign } from '@atds-legacy/lang';
import { Record, Resource, SDK, deserialize } from '@redge-media/fo-js-sdk';
import { CookieName, HeaderName, ProductType } from '@/shared/enums';
import { changeEpisodeProperties, filterBookmarks, sortBookmarks } from '@/shared/utils';
import { generateCookie } from './bookmark-cookie';
import destroy from './destroy';
import remove from './remove';

const Bookmarks = Resource.mixin(destroy, remove).extend({
  endpoint: 'int/subscribers/bookmarks?type=WATCHED',
  cookieName: CookieName.WATCHED_HASH,
  continueWatching: [],

  constructor: function Bookmarks(subscriber, time, info) {
    Resource.call(assign(this, {
      subscriber,
      time,
      info,
    }));

    this.on('sync', this.updateContinueWatching);
  },

  deserialize(response, opts = {}) {
    const data = Resource.prototype.deserialize.call(this, response, opts);
    const detailedItems = data.continueWatchingItems.reduce((byId, { item }) => {
      byId[item.id] = item;
      return byId;
    }, {});

    data.items.forEach((record) => {
      const detailedItem = detailedItems[record.item.id] || {};
      Object.assign(record.item, detailedItem);
    });
    return data.items;
  },

  getPath() {
    return 'subscribers/bookmarks/continue/watching';
  },

  refresh({ cookieRefresh }) {
    if (cookieRefresh) {
      generateCookie({
        id: this.subscriber.id,
        name: this.cookieName,
        time: this.time.now,
      });
    }

    if (this.subscriber.isLogged) {
      return this.fetchAll({
        kids: this.subscriber.isKidsTheme,
        hash: Cookie.get(this.cookieName),
      }, { reset: true });
    }

    return this.collection.reset();
  },

  createRecord(props = {}, opts = {}) {
    if (props instanceof Record) {
      return props;
    }

    const item = props.item || props;

    item.bookmark = {
      createdAt: dayjs(props.createdAt),
      modifiedAt: dayjs(props.modifiedAt),
      playTime: item.status.bookmark,
      progress: item.status.progressWatching,
      watched: !!props.watchedAt,
    };

    return SDK.factory.resolve(item, opts);
  },

  createBookmark(record, playTime, watched) {
    const progress = Math.floor((playTime / record.duration) * 100);
    const product = this.collection.get(record.id) || record;

    assign(product, {
      bookmark: {
        createdAt: product.bookmark?.createdAt || this.time.now,
        modifiedAt: this.time.now,
        playTime,
        progress,
        watched,
      },
    });

    return product;
  },

  create(record, opts = {}) {
    const method = 'post';
    const { itemId, playTime, watched } = opts;

    return SDK.apiAdapter[method](this.endpoint, {
      itemId,
      playTime,
      profile: {
        token: this.subscriber.profile.token,
        id: this.subscriber.profile.id,
        name: this.subscriber.profile.name,
      },
    }, {
      headers: {
        [HeaderName.SUBSCRIBER_HASH]: Cookie.get(CookieName.SUBSCRIBER_HASH),
        [HeaderName.SUBSCRIBER_PUB]: Cookie.get(CookieName.SUBSCRIBER_PUB),
      },
    }).then(() => {
      if (!opts.refresh) {
        if (record.duration) {
          const product = this.createBookmark(record, playTime, watched);
          this.collection.add(product, { parse: true, isFetched: true });
          this.emit('sync', method, record);
        }
      }

      generateCookie({
        id: this.subscriber.id,
        name: this.cookieName,
        time: this.time.now,
      });

      return Promise.resolve(record);
    });
  },

  getContinueWatchingEpisode(serialId) {
    return SDK.apiAdapter.get(`/subscribers/bookmarks/continue/watching/${serialId}?type=WATCHED&itemType=SERIAL`)
      .then((response) => deserialize(response).item);
  },

  updateContinueWatching() {
    const bookmarks = this.collection.records;
    const maxLastModifiedTimeInHours = this.info.platformSettings?.maxContinueWatchingAgeInHours;
    const recentBookmarks = bookmarks.filter(({ bookmark }) => {
      const modifiedAtInHours = dayjs().diff(dayjs(bookmark.modifiedAt), 'hour');
      return modifiedAtInHours < maxLastModifiedTimeInHours;
    });
    const episodesBookmarks = recentBookmarks.filter(
      ({ type, season }) => type === ProductType.EPISODE && season,
    );

    this.continueWatching = changeEpisodeProperties(
      sortBookmarks(filterBookmarks(recentBookmarks)),
      episodesBookmarks,
    );
  },
});

export default Bookmarks;
